// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import {
    components,
    GroupBase,
    GroupProps,
    MenuProps,
    PropsValue,
} from 'react-select';
import { ISelectRequiredOption } from 'common/components';
import {
    Translate,
    useTranslate,
} from '@plesk/ui-library';
import SelectInput from 'common/components/Select/SelectInput';
import { FixedFormField } from 'admin/common/components/Filters/Styles';
import { FILTERS } from 'admin/common/constants/tests';
import { COMPUTE_RESOURCE_HA_STATUS } from 'admin/computeResource/constants';
import { failoverDomains } from 'common/api/resources/FailoverDomain';
import { COLORS } from 'common/theme';

export const HA_FILTER_GROUP = 'ha_status';
export const FD_FILTER_GROUP = 'fd';

export interface IHaFilterOption extends ISelectRequiredOption {
    group: typeof HA_FILTER_GROUP | typeof FD_FILTER_GROUP;
}

interface IHighAvailabilityFilterProps {
    onChange: (option: PropsValue<IHaFilterOption>) => void;
    value?: IHaFilterOption;
}

export const HA_STATUS_TRANSLATION_MAP: Record<COMPUTE_RESOURCE_HA_STATUS, string> = {
    [COMPUTE_RESOURCE_HA_STATUS.PROTECTED]: 'computeResource.servers.haStatuses.protected',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_PROTECTED]: 'computeResource.servers.haStatuses.notProtected',
    [COMPUTE_RESOURCE_HA_STATUS.AT_RISK]: 'computeResource.servers.haStatuses.atRisk',
    [COMPUTE_RESOURCE_HA_STATUS.UNAVAILABLE]: 'computeResource.servers.haStatuses.unavailable',
    [COMPUTE_RESOURCE_HA_STATUS.ELIGIBLE]: 'computeResource.servers.haStatuses.eligible',
    [COMPUTE_RESOURCE_HA_STATUS.NOT_ELIGIBLE]: 'computeResource.servers.haStatuses.notEligible',
    [COMPUTE_RESOURCE_HA_STATUS.PAUSED]: 'computeResource.servers.haStatuses.paused',
};

const GroupWithSeparator = (
    props: GroupProps<IHaFilterOption, false, GroupBase<IHaFilterOption>>
) => {
    const isFirstGroup = props.selectProps.options?.[0] === props.data;
    return (
        <div style={isFirstGroup ? undefined : {
            borderTop: `1px solid ${COLORS.GREY_40}`,
            marginTop: 4,
            paddingTop: 4,
        }}>
            <components.Group {...props} />
        </div>
    );
};

const WideMenu = (
    props: MenuProps<IHaFilterOption, false, GroupBase<IHaFilterOption>>
) => (
    <components.Menu {...props}>
        <div style={{ minWidth: 220 }}>
            {props.children}
        </div>
    </components.Menu>
);

const SELECT_COMPONENTS = {
    Group: GroupWithSeparator,
    GroupHeading: () => null,
    Menu: WideMenu,
};

export const HighAvailabilityFilter: React.FC<IHighAvailabilityFilterProps> = ({
    onChange,
    value,
}) => {
    const translate = useTranslate();
    const [fdOptions, setFdOptions] = React.useState<IHaFilterOption[]>([]);

    const haOptions: IHaFilterOption[] = React.useMemo(() => Object.values(COMPUTE_RESOURCE_HA_STATUS).map(status => ({
        value: status,
        label: translate(HA_STATUS_TRANSLATION_MAP[status]) as string,
        group: HA_FILTER_GROUP,
    })), [translate]);

    React.useEffect(() => {
        (async () => {
            const response = await failoverDomains.list({
                filters: { search: undefined },
            });
            setFdOptions(response.data.data.map(item => ({
                value: item.id.toString(),
                label: item.name,
                group: FD_FILTER_GROUP,
            })));
        })();
    }, []);

    const groupedOptions: Array<GroupBase<IHaFilterOption>> = React.useMemo(() => [
        { label: '', options: haOptions },
        ...(fdOptions.length > 0 ? [{ label: '', options: fdOptions }] : []),
    ], [haOptions, fdOptions]);

    return (
        <FixedFormField data-cy={FILTERS.HIGH_AVAILABILITY}>
            <SelectInput<IHaFilterOption, false, GroupBase<IHaFilterOption>>
                inputId="high-availability-filter"
                placeholder={<Translate content="filters.highAvailability" />}
                options={groupedOptions}
                isClearable={true}
                onChange={onChange}
                value={value}
                menuPosition="absolute"
                menuPortalTarget={document.body}
                maxMenuHeight={400}
                components={SELECT_COMPONENTS}
            />
        </FixedFormField>
    );
};
